import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import find from 'lodash-es/find';
import orderBy from 'lodash-es/orderBy';

import {Payrolls} from '../../utils/payrolls';
import {PayPeriodContract, PayPeriodContractAction, PayStatus} from '../employees.model';

@Injectable({
  providedIn: 'root'
})
export class PayPeriodContractUtilsService {

  constructor(
    private translate: TranslateService) {
  }

  private statusSortValues = [
    {status: PayStatus.TODO, value: 0},
    {status: PayStatus.CLOSED, value: 1},
    {status: PayStatus.FROZEN, value: 2},
    {status: PayStatus.DONE, value: 3},
    {status: PayStatus.AGAIN, value: 4},
    {status: PayStatus.ALERT, value: 5},
    {status: PayStatus.ERROR, value: 6},
    {status: PayStatus.ONGOING, value: 7},
    {status: PayStatus.SCHEDULED, value: 8}
  ];

  public formatPayPeriodContractsToDisplayBadges(payPeriodContracts: PayPeriodContract[]): PayPeriodContract[] {
    const result: PayPeriodContract[] = [];
    payPeriodContracts.forEach(payPeriodContract => {
      result.push(this.formatPayPeriodContractToDisplayBadge(payPeriodContract));
    });
    return result;
  }

  public findWorstPayPeriodContract(payPeriodContracts: PayPeriodContract[]): PayPeriodContract {
    if (!payPeriodContracts || (payPeriodContracts && payPeriodContracts.length < 1)) {
      return;
    }

    let worstPayPeriodContract = {payPeriodContract: null, value: null};
    worstPayPeriodContract = this.findAndGetWorstPayPeriodContract(payPeriodContracts, this.statusSortValues, worstPayPeriodContract);
    return worstPayPeriodContract.payPeriodContract;
  }

  public isActionInAnyPayPeriodContract(payPeriodContracts: PayPeriodContract[], action: PayPeriodContractAction): boolean {
    let result = false;
    if (!payPeriodContracts) {
      return result;
    }
    payPeriodContracts.forEach(payPeriodContract => {
      if (this.isActionInPayPeriodContract(payPeriodContract, action)) {
        result = true;
      }
    });
    return result;
  }

  public isActionInPayPeriodContract(payPeriodContract: PayPeriodContract, action: PayPeriodContractAction): boolean {
    return payPeriodContract.actions && payPeriodContract.actions.includes(action);
  }

  public isLastExecInAnyPayPeriodContract(payPeriodContracts: PayPeriodContract[]): boolean {
    let result = false;
    if (!payPeriodContracts) {
      return result;
    }
    payPeriodContracts.forEach(payPeriodContract => {
      if (this.isLastExecInPayPeriodContract(payPeriodContract)) {
        result = true;
      }
    });
    return result;
  }

  public isLastExecInPayPeriodContract(payPeriodContract: PayPeriodContract): boolean {
    return !!(payPeriodContract.lastExecution && payPeriodContract.lastExecution.id);
  }

  public sortPayPeriodContracts(payPeriodContracts: PayPeriodContract[]): PayPeriodContract[] {
    if (!payPeriodContracts) {
      return [];
    }
    return orderBy(payPeriodContracts,
      [payPeriodContract => payPeriodContract.payTypeTransId.toLowerCase()],
      ['asc']);
  }

  public groupByPayTypeCalculationType(payPeriodContracts: PayPeriodContract[]): Map<string, PayPeriodContract[]> {
    return payPeriodContracts.reduce((payTypesMap, payPeriodContract: PayPeriodContract) => {
      if (!payTypesMap.has(payPeriodContract.payTypeCalculationType)) {
        payTypesMap.set(payPeriodContract.payTypeCalculationType, []);
      }
      payTypesMap.get(payPeriodContract.payTypeCalculationType).push(payPeriodContract);
      return payTypesMap;
    }, new Map<string, PayPeriodContract[]>());
  }

  private findAndGetWorstPayPeriodContract(payPeriodContracts: PayPeriodContract[], sortValues, worstPayPeriodContract) {
    const result = worstPayPeriodContract;
    payPeriodContracts.forEach(payPeriodContract => {
      const statusSortValue = find(sortValues, sortValue => {
        return Payrolls.getDisplayNameForStatus(payPeriodContract.status, this.translate) === Payrolls.getDisplayNameForStatus(sortValue.status, this.translate);
      });
      if (!!statusSortValue && statusSortValue.value >= result.value) {
        result.payPeriodContract = payPeriodContract;
        result.payTypeName = payPeriodContract.payTypeName;
        result.value = statusSortValue.value;
      }
    });

    return result;
  }

  private formatPayPeriodContractToDisplayBadge(payPeriodContract: PayPeriodContract): PayPeriodContract {
    const result: PayPeriodContract = payPeriodContract;
    result.statusToDisplay = '';
    result.badgeCssClasses = [];

    switch (payPeriodContract.status) {
      case PayStatus.AGAIN:
        result.badgeCssClasses.push('label-again');
        result.statusToDisplay = 'COMMON.again';
        break;
      case PayStatus.ALERT:
        result.badgeCssClasses.push('label-alert');
        result.statusToDisplay = 'COMMON.alert';
        break;
      case PayStatus.DONE:
        result.badgeCssClasses.push('label-done');
        result.statusToDisplay = 'COMMON.done';
        break;
      case PayStatus.ERROR:
        result.badgeCssClasses.push('label-error');
        result.statusToDisplay = 'COMMON.error';
        break;
      case PayStatus.ONGOING:
        result.badgeCssClasses.push('label-ongoing');
        result.statusToDisplay = 'COMMON.ongoing';
        break;
      case PayStatus.FROZEN:
        result.badgeCssClasses.push('label-froze');
        result.statusToDisplay = 'COMMON.frozen';
        break;
      case PayStatus.READY:
        result.badgeCssClasses.push('label-ready');
        result.statusToDisplay = 'COMMON.ready';
        break;
      case PayStatus.SCHEDULED:
      case PayStatus.SENDED:
        result.badgeCssClasses.push('label-scheduled');
        result.statusToDisplay = 'COMMON.scheduled';
        break;
      case PayStatus.TODO:
        result.badgeCssClasses.push('label-to-do');
        result.statusToDisplay = 'COMMON.todo';
        break;
    }

    return result;
  }


}
